import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { useCloseDrawer } from "@hooks/use-drawer";
import { useReloadUser } from "@hooks/use-modify-user";
import useCustomizableCloset, { useReloadCustomizableCloset } from "@hooks/use-customizable-closet";
import Image from "@components/core/Image";
import CustomLoader from "@components/core/CustomLoader";
import { Button } from "@components/core";
import { getReturnClosetState } from "@utils/call-api/closets-call";

function CheckReturnParcelAnimation() {
  const t = useTranslations("common");

  const closet = useCustomizableCloset();
  const reloadCurrentCloset = useReloadCustomizableCloset();
  const reloadUser = useReloadUser();
  const closeDrawer = useCloseDrawer();
  const closetUID = closet?.uid;

  const [loading, setLoading] = useState(false);
  const [closetState, setClosetState] = useState("");

  function displayCustomLoader() {
    return (
      <div className="d-flex flex-column align-items-center bg-white py-4 px-3 text-center h-100">
        <div className="d-flex flex-column justify-content-center align-items-center h-100">
          <div className="d-flex flex-column align-items-center justify-content-center gap-4 px-4 text-center mb-5">
            <CustomLoader
              variant="Primary"
              style={{ width: "120px" }}
            />
            <p className="font-weight-bold text-gray">
              {t("drawer.check_return_parcel_animation.we_check_your_return_parcel")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  function displayConfirmedPostReturn() {
    return [
      <div
        className="drawer-body bg-white"
        key="drawer-animation-result-body"
      >
        <div className="d-flex flex-column align-items-center p-4 m-2 text-center">
          <h5 className="mb-0">
            {t("drawer.check_return_parcel_animation.your_return_is_confirmed")}
          </h5>
          <Image
            src="unlockedBagNature"
            alt="unlocked-bag"
            className="mt-4 pb-3"
            width={120}
            height={140}
          />
        </div>
      </div>,
      <div
        className="bg-white p-3 border-top"
        key="drawer-animation-result-footer"
      >
        <Button
          fullWidth
          variant="primary"
          onClick={reloadCurrentCloset}
        >
          {t("drawer.check_return_parcel_animation.got_it")}
        </Button>
      </div>,
    ];
  }

  function displayComeBackLater() {
    return [
      <div
        className="drawer-body bg-white"
        key="drawer-animation-result-body"
      >
        <div className="d-flex flex-column align-items-center p-4 m-2 text-center">
          <h5 className="mb-2">
            {t("drawer.check_return_parcel_animation.come_back_later")}
          </h5>
          <p className="text-gray mb-0">
            {t("drawer.check_return_parcel_animation.please_try_again_in_a_few_minutes")}
          </p>
          <Image
            src="lockedBagNature"
            alt="locked-bag"
            className="my-4 pb-4"
            width={120}
            height={140}
          />
          <p className="text-gray">
            {t("drawer.check_return_parcel_animation.we_will_let_know_as_soon")}
          </p>
        </div>
      </div>,
      <div
        className="bg-white p-3 border-top"
        key="drawer-animation-result-footer"
      >
        <Button
          fullWidth
          variant="primary"
          onClick={closeDrawer}
        >
          {t("drawer.check_return_parcel_animation.got_it")}
        </Button>
      </div>,
    ];
  }

  function renderResult() {
    switch (closetState) {
      case "received":
        return displayComeBackLater();
      case "post_returned":
        return displayConfirmedPostReturn();
      default:
        return displayComeBackLater();
    }
  }

  useEffect(() => {
    async function startCheckReturnParcel() {
      if (!closetUID) return;

      const minSearchTime = 5000;
      const startTime = new Date().getTime();

      setLoading(true);

      try {
        const closetStateRes = await getReturnClosetState(closetUID);

        setClosetState(closetStateRes.closet_state);
      } catch (e) {
        setClosetState(closet?.state);
      } finally {
        reloadCurrentCloset();
        reloadUser();

        const endTime = new Date().getTime();
        const responseTime = endTime - startTime;
        const remainingTime = Math.max(0, minSearchTime - responseTime);
        setTimeout(() => {
          setLoading(false);
        }, remainingTime);
      }
    }

    startCheckReturnParcel();
  }, []);

  if (loading) return displayCustomLoader();

  return renderResult();
}

export default CheckReturnParcelAnimation;
